import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function PlanCard({ plan, currentMembership, isPopular, allPlans = [] }) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const isCommunityPlan = plan.name === 'Community Member';
  const isCurrentPlan = currentMembership?.membership_type?.id === plan.id;

  // Determine if this plan can be selected based on current membership
  const isPlanSelectable = () => {
    if (isCurrentPlan) return false; // Current plan cannot be selected

    if (!currentMembership) return true; // No membership, all plans selectable

    const currentPlanName = currentMembership.membership_type?.name;
    const targetPlanName = plan.name;

    // Community user: Can select both Quarterly Pro and Annual Pro
    if (currentPlanName === 'Community Member') {
      return true; // All plans available
    }

    // Quarterly Pro user: Can only select Annual Pro (no downgrade to Community)
    if (currentPlanName === 'Pro Member - Quarterly') {
      if (targetPlanName === 'Community Member') return false; // Block downgrade
      if (targetPlanName === 'Pro Member - Annual') return true; // Allow upgrade
      return false; // Block same plan
    }

    // Annual Pro user: Can only select Family Pro (no downgrade to Community or Quarterly)
    if (currentPlanName === 'Pro Member - Annual') {
      if (targetPlanName === 'Pro Member - Family') return true; // Allow upgrade
      return false; // Block all downgrades
    }

    // Family Pro user: nothing to upgrade to; downgrades are not supported
    if (currentPlanName === 'Pro Member - Family') {
      return false;
    }

    return true; // Default: allow
  };

  const isSelectable = isPlanSelectable();
  const isDisabled = isCurrentPlan || !isSelectable;

  // Calculate savings for annual plan dynamically
  const calculateSavings = () => {
    // Only for paid annual plans (exclude Community Member)
    if (plan.duration_months !== 12 || isCommunityPlan) return 0;
    
    // Find the quarterly plan to compare against
    const quarterlyPlan = allPlans.find(p => p.duration_months === 3 && p.price > 0);
    if (!quarterlyPlan) return 0;
    
    // Calculate yearly cost of quarterly plan vs annual plan
    const quarterlyYearlyCost = quarterlyPlan.price * 4; // 4 quarters in a year
    const annualCost = plan.price;
    
    return Math.max(0, quarterlyYearlyCost - annualCost);
  };
  
  const savings = calculateSavings();
  
  // Format price display
  const formatPrice = (price, duration) => {
    if (price === 0) return 'Free';
    
    const periods = {
      3: 'quarter',
      12: 'year'
    };
    
    return `$${price} / ${periods[duration] || 'period'}`;
  };

  const handleSelectPlan = () => {
    if (isDisabled) return;

    // All paid plans go to upgrade page
    // (Free Community plan button is always disabled since users auto-get it on signup)
    navigate('/upgrade', {
      state: {
        suggestedPlan: plan,
        currentMembership
      }
    });
  };

  const getButtonText = () => {
    if (isCurrentPlan) return 'Current Plan';

    // Keep original button text even when not selectable
    if (isCommunityPlan) return 'Join Community';
    if (currentMembership?.membership_type?.name === 'Community Member') return 'Upgrade to Pro';
    if (currentMembership?.membership_type?.name === 'Pro Member - Quarterly' && plan.name === 'Pro Member - Annual') {
      return 'Upgrade to Annual';
    }
    if (currentMembership?.membership_type?.name === 'Pro Member - Annual' && plan.name === 'Pro Member - Family') {
      return 'Upgrade to Family';
    }
    return 'Select Plan';
  };

  const getButtonStyle = () => {
    if (isDisabled) {
      return 'bg-gray-100 text-gray-500 cursor-not-allowed';
    }
    if (isCommunityPlan) {
      return 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500';
    }
    return 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';
  };

  return (
    <div
      className={`relative bg-white rounded-2xl shadow-lg transition-all duration-200 flex flex-col h-full ${
        isHovered ? 'shadow-xl scale-105' : ''
      } ${isPopular ? 'ring-2 ring-blue-500' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Popular Badge */}
      {isPopular && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
            Best Value
          </div>
        </div>
      )}

      <div className="p-8 flex flex-col flex-grow">
        {/* Plan Header */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {plan.name}
          </h3>
          
          <div className="mb-4">
            <span className="text-4xl font-bold text-gray-900">
              {plan.price === 0 ? 'Free' : `$${plan.price}`}
            </span>
            {plan.price > 0 && (
              <span className="text-gray-500 ml-2">
                / {plan.duration_months === 3 ? '3 months' : 'year'}
              </span>
            )}
            {/* Savings inline with price for annual plans only */}
            {savings > 0 && plan.duration_months === 12 && (
              <span className="ml-2 text-green-600 font-medium text-lg">
                (Save ${savings})
              </span>
            )}
          </div>

          <p className="text-gray-600 text-lg mb-6">
            {plan.description}
          </p>
        </div>

        {/* Features List */}
        <div className="space-y-4 mb-8 flex-grow">
          {plan.benefits?.features?.map((feature, index) => (
            <div key={index} className="flex items-start">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="ml-3 text-gray-700 leading-relaxed">{feature}</p>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <button
          onClick={handleSelectPlan}
          disabled={isDisabled}
          title={!isCurrentPlan && !isSelectable ? 'Available when current plan ends' : ''}
          className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${getButtonStyle()}`}
        >
          {getButtonText()}
        </button>

        {/* Additional Info for All Plans */}
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">
            {isCommunityPlan
              ? 'Free for everyone'
              : plan.duration_months === 12
                ? 'Billed annually'
                : 'Billed quarterly'
            }
          </p>
        </div>
      </div>

      {/* Current Plan Indicator */}
      {isCurrentPlan && (
        <div className="absolute top-4 right-4">
          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            Current
          </div>
        </div>
      )}
    </div>
  );
}

export default PlanCard;